import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import Page from "components/Layout/Page";
import SimpleField from "components/SimpleField";

import ROUTES from "navigation/routes";

import {
  SnackbarContextProps,
  withGlobalSnackbar,
} from "services/snackbar/snackbarContext";

import {
  BackButtonWrapper,
  Column,
  Container,
  ControlContainer,
  ControlsContainer,
  Divider,
  GroupRow,
  Row,
} from "./styles";

import UserProfileStore, {
  BodyGramStore,
  CoachReport,
  UserProfileDataStore,
  UserProfileFoodPlanStore,
  UserProfileMeasurementStore,
  UserProfileQuizAnswerStore,
  UserProfileQuizStore,
} from "stores/UserProfileStore";
import { Button, Paper, Tab, Tabs } from "@material-ui/core";
import TabPanel from "./TabPanel";

import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import FormControl from "components/FormControl";
import arrayMutators from "final-form-arrays";
import { FormSpy } from "react-final-form";
import QuizAnswer from "./QuizDetail/QuizAnswer";
import { SNACKBAR_TYPES } from "components/Snackbar";
import UsersStore from "../../../../../stores/UsersStore";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import ConfirmationDialog from "components/ConfirmationDialog";
import BodyGramDetail from "./BodyGramDetail";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ userId: string }, {}, LocationState> &
  SnackbarContextProps &
  GlobalPopupProps & {};

@observer
class UserDetail extends React.Component<Props> {
  @computed get resetPasswordText() {
    return this.displayValues?.name
      ? `Are you sure you want to reset the password for ${this.displayValues?.name} to the default one?`
      : ``;
  }

  @computed get resetAccountText() {
    return this.displayValues?.name
      ? `Are you sure you want to reset the account for ${this.displayValues?.name}?`
      : ``;
  }

  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable valueIndex: number = 0;
  @observable bodyGramId: number = 0;
  @observable onAccept: () => void = () => {};

  @computed get quiz() {
    return UserProfileQuizStore.items;
  }

  @computed get bodyGramLogs() {
    return BodyGramStore.items;
  }

  @computed get answers() {
    return UserProfileQuizAnswerStore.items;
  }

  get locationState() {
    return this.props.location.state;
  }

  get userId() {
    const { userId } = this.props.match.params;

    const parsedUserId = Number.parseInt(userId);
    return typeof parsedUserId === "number" ? parsedUserId : undefined;
  }

  get backClickInfo() {
    return " Back to Users";
  }

  get backClickPath() {
    return ROUTES.CONTENT_PROFILE_USER;
  }

  @computed get displayValues(): any {
    const formData = UserProfileStore.details?.formData;
    const formProfileData = UserProfileDataStore?.formData;
    const formProfileMeasurementData = UserProfileMeasurementStore?.formData;
    const formProfileFoodPlanData = UserProfileFoodPlanStore?.formData;
    const formProfileQuizData = UserProfileQuizStore?.items;

    return {
      ...formData,
      ...formProfileData,
      ...formProfileMeasurementData,
      ...formProfileFoodPlanData,
      ...formProfileQuizData,
    };
  }

  @computed get displayValuesCoach(): any {
    const formCoachReportData = CoachReport?.formData.data;

    return {
      ...formCoachReportData,
    };
  }

  @computed get initialValues() {
    return {
      ...UserProfileStore.details?.formData,
    };
  }

  @computed get quizOptions() {
    const quizOptions =
      this.quiz?.map((item: any) => ({
        value: item.id,
        label: item.id,
      })) ?? [];

    return [...quizOptions];
  }

  @computed get bodyGramLogsOptions() {
    const quizOptions =
      this.bodyGramLogs?.map((item: any) => ({
        value: item.id,
        label: item.scanningSessionId,
      })) ?? [];

    return [...quizOptions];
  }

  async componentDidMount() {
    await Promise.all([
      UserProfileStore.fetchUserDetails(this.userId),
      UserProfileDataStore.fetchProfileData(this.userId),
      UserProfileMeasurementStore.fetchProfileData(this.userId),
      UserProfileFoodPlanStore.fetchProfileData(this.userId),
      UserProfileQuizStore.fetch(this.userId),
      CoachReport.fetchCoachData(this.userId),
      BodyGramStore.fetch(this.userId),
    ]);
  }

  componentWillUnmount() {
    UserProfileStore.resetDetails();
    UserProfileDataStore.reset();
    UserProfileMeasurementStore.reset();
    UserProfileFoodPlanStore.reset();
    UserProfileQuizAnswerStore.reset();
    CoachReport.reset();
    // UserProfileDataStore.resetDetails();
  }

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    onAccept();
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  onReset = (close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      if (this.displayValues?.email) {
        await UsersStore.resetPassword(this.displayValues?.email);
      }

      close();
      setMessage("Password reset successfully", SNACKBAR_TYPES.SUCCESS);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onResetAccount = (close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      if (this.userId) {
        await UserProfileStore.resetOnboarding(this.userId);
      }

      close();
      setMessage("User account successfully", SNACKBAR_TYPES.SUCCESS);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onResetPasswordClick = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "ShowScheduleInfo",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Reset Password"
          text={this.resetPasswordText}
          declineText="Cancel"
          acceptText="Confirm"
          onDecline={close}
          onAccept={this.onReset(close)}
          isLoading={UsersStore.isManaging}
        />
      ),
    });
  };

  onResetAccountClick = () => {
    const { openPopup } = this.props;

    openPopup({
      key: "ShowScheduleInfo",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Reset Account"
          text={this.resetAccountText}
          declineText="Cancel"
          acceptText="Confirm"
          onDecline={close}
          onAccept={this.onResetAccount(close)}
          isLoading={UsersStore.isManaging}
        />
      ),
    });
  };

  get renderAnswer(): any {
    let dataStr = <></>;
    if (this.quizId > 0) {
      UserProfileQuizAnswerStore.fetch(this.userId, this.quizId);
      dataStr = (
        <GroupRow>
          <Column>
            {this.answers?.map((item, index) => (
              <Row key={index}>
                <Column key={index} width={"100%"}>
                  <SimpleField label={item.title} value={item.answer} />
                </Column>
              </Row>
            ))}
          </Column>
        </GroupRow>
      );
    }
    return dataStr;
  }

  render() {
    return (
      <Page
        title="User Details"
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickInfo}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={UserProfileStore.isUserLoading || UsersStore.isManaging}
      >
        <Container>
          <ControlsContainer>
            <ControlContainer>
              <Button
                variant="contained"
                color="primary"
                onClick={this.onResetAccountClick}
              >
                {"Reset Account"}
              </Button>
            </ControlContainer>

            <ControlContainer>
              <Button
                variant="contained"
                color="primary"
                onClick={this.onResetPasswordClick}
              >
                {"Reset Password"}
              </Button>
            </ControlContainer>
          </ControlsContainer>
          <Paper>
            <Tabs
              value={this.valueIndex}
              onChange={(event, newValue) => {
                this.valueIndex = newValue;
              }}
              indicatorColor="primary"
              textColor="primary"
              centered
            >
              <Tab label="Profile" />
              <Tab label="Measurements" />
              <Tab label="Food Plan" />
              <Tab label="Quiz" />
              <Tab label="Bodygram" />
            </Tabs>
          </Paper>
          <TabPanel value={this.valueIndex} index={0}>
            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    <SimpleField
                      label="Email"
                      value={this.displayValues?.email}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Phone"
                      value={this.displayValues?.phoneNumber}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Active Program"
                      value={this.displayValues?.activeProgram?.name}
                    />
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider light={false} />

            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    <SimpleField
                      label="Name"
                      value={this.displayValues?.name}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Gender"
                      value={this.displayValues?.genderText}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Phone"
                      value={this.displayValues?.phone}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Date of Birth"
                      value={this.displayValues?.birthDate}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Location"
                      value={this.displayValues?.location}
                    />
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider light={false} />

            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    <SimpleField
                      label="Coach Name"
                      value={this.displayValuesCoach?.name}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Coach Email"
                      value={this.displayValuesCoach?.email}
                    />
                  </Column>
                </Row>
              </Column>
            </GroupRow>
          </TabPanel>
          <TabPanel value={this.valueIndex} index={1}>
            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    <SimpleField
                      label="Bust"
                      value={this.displayValues?.bust}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Waist"
                      value={this.displayValues?.waist}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Hips"
                      value={this.displayValues?.hips}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Butt"
                      value={this.displayValues?.butt}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Right Thigh"
                      value={this.displayValues?.rightThigh}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Left Thigh"
                      value={this.displayValues?.leftThigh}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Right Arm"
                      value={this.displayValues?.rightArm}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Left Arm"
                      value={this.displayValues?.leftArm}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Right Calf"
                      value={this.displayValues?.rightCalf}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Left Calf"
                      value={this.displayValues?.leftCalf}
                    />
                  </Column>
                </Row>
              </Column>
            </GroupRow>
          </TabPanel>
          <TabPanel value={this.valueIndex} index={2}>
            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    <SimpleField
                      label="Heigh (in)"
                      value={this.displayValues?.height}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Heigh (cm)"
                      value={this.displayValues?.heightInCm}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SimpleField
                      label="Weight (lb)"
                      value={this.displayValues?.weight}
                    />
                  </Column>
                  <Column>
                    <SimpleField
                      label="Weight (kg)"
                      value={this.displayValues?.weightInKg}
                    />
                  </Column>
                </Row>
              </Column>
            </GroupRow>
          </TabPanel>
          <TabPanel value={this.valueIndex} index={3}>
            <GroupRow>
              <FormControl
                onSubmit={() => {}}
                mutators={{ ...arrayMutators }}
                initialValues={this.initialValues}
                keepDirtyOnReinitialize={false}
              >
                <FormSpy subscription={{ values: true }}>
                  {({ values }) => {
                    this.quizId = values.quizId;
                    const quizUserId = values.quizId;
                    return (
                      <Column>
                        <Row>
                          <Column>
                            <DropdownFieldControl
                              name="quizId"
                              options={this.quizOptions}
                              value={this.quizId}
                              label="Quizzes"
                              isClearable
                              multi={false}
                            />
                          </Column>
                        </Row>
                        <>
                          {quizUserId && (
                            <QuizAnswer
                              userId={this.userId}
                              quizId={quizUserId}
                            />
                          )}
                        </>
                      </Column>
                    );
                  }}
                </FormSpy>
              </FormControl>
            </GroupRow>
          </TabPanel>
          <TabPanel value={this.valueIndex} index={4}>
            <GroupRow>
              <FormControl
                onSubmit={() => {}}
                mutators={{ ...arrayMutators }}
                initialValues={this.initialValues}
                keepDirtyOnReinitialize={false}
              >
                <FormSpy subscription={{ values: true }}>
                  {({ values }) => {
                    this.bodyGramId = values.bodyGramId;
                    const bodyGramUserId = values.bodyGramId;
                    return (
                      <Column>
                        <Row>
                          <Column>
                            <DropdownFieldControl
                              name="bodyGramId"
                              options={this.bodyGramLogsOptions}
                              value={this.bodyGramId}
                              label="Bodygram Logs"
                              isClearable
                              multi={false}
                            />
                          </Column>
                        </Row>
                        <>
                          {bodyGramUserId && (
                            <BodyGramDetail
                              userId={this.userId}
                              bodyGramId={bodyGramUserId}
                            />
                          )}
                        </>
                      </Column>
                    );
                  }}
                </FormSpy>
              </FormControl>
            </GroupRow>
          </TabPanel>
        </Container>
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(UserDetail)));
